import type { IPosition, TBoard, TPlayer } from '../types';
import { BOARD_SIZE, WIN_LENGTH } from '../constants';

interface IWinCheckResult {
  hasWinner: boolean;
  winner: TPlayer | null;
  winningLine: IPosition[] | null;
}

export function checkWinner(board: TBoard): IWinCheckResult {
  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      const cell = board[row][col];
      if (cell === null) continue;

      const horizontal = checkLine(board, row, col, 0, 1, cell);
      if (horizontal.hasWinner) return horizontal;

      const vertical = checkLine(board, row, col, 1, 0, cell);
      if (vertical.hasWinner) return vertical;

      const diagonal1 = checkLine(board, row, col, 1, 1, cell);
      if (diagonal1.hasWinner) return diagonal1;

      const diagonal2 = checkLine(board, row, col, 1, -1, cell);
      if (diagonal2.hasWinner) return diagonal2;
    }
  }

  return { hasWinner: false, winner: null, winningLine: null };
}

export function hasWinningLineThroughCell(
  board: TBoard,
  row: number,
  col: number,
  player: TPlayer,
): boolean {
  if (board[row]?.[col] !== player) return false;

  const directions: readonly [number, number][] = [
    [0, 1],
    [1, 0],
    [1, 1],
    [1, -1],
  ];

  for (const [dr, dc] of directions) {
    let count = 1;
    let r = row + dr;
    let c = col + dc;
    while (
      r >= 0 &&
      r < BOARD_SIZE &&
      c >= 0 &&
      c < BOARD_SIZE &&
      board[r]?.[c] === player
    ) {
      count += 1;
      r += dr;
      c += dc;
    }
    r = row - dr;
    c = col - dc;
    while (
      r >= 0 &&
      r < BOARD_SIZE &&
      c >= 0 &&
      c < BOARD_SIZE &&
      board[r]?.[c] === player
    ) {
      count += 1;
      r -= dr;
      c -= dc;
    }
    if (count >= WIN_LENGTH) return true;
  }

  return false;
}

function checkLine(
  board: TBoard,
  startRow: number,
  startCol: number,
  rowDir: number,
  colDir: number,
  player: TPlayer,
): IWinCheckResult {
  const line: IPosition[] = [];

  for (let i = 0; i < WIN_LENGTH; i++) {
    const row = startRow + i * rowDir;
    const col = startCol + i * colDir;

    if (row < 0 || row >= BOARD_SIZE || col < 0 || col >= BOARD_SIZE) {
      return { hasWinner: false, winner: null, winningLine: null };
    }

    if (board[row][col] !== player) {
      return { hasWinner: false, winner: null, winningLine: null };
    }

    line.push({ row, col });
  }

  return { hasWinner: true, winner: player, winningLine: line };
}
